import React, { useEffect, useMemo, useRef } from "react";

import { useListContext } from "./List.hooks";

import { cn } from "@/components/ui/utils";
import { Checkbox } from "@/components/ui/checkbox";

/** A row's words, at the list's size: its level's, the theme's. */
const ROW_TEXT = {
  sm: "font-medium text-xs leading-snug text-foreground",
  md: "font-medium text-sm leading-snug text-foreground",
  lg: "font-medium text-lg leading-snug text-foreground",
} as const;

export function ListItem<T>({
  value,
  children,
  className,
  "data-testid": dataTestId,
}: {
  value: T;
  children?: React.ReactNode;
  className?: string;
  "data-testid"?: string;
}) {
  const ref = useRef<HTMLLIElement>(null);
  const {
    getRowKey,
    getShouldSkipRow,
    isMultiselect,
    isCheckboxesEnabled,
    isNullable,
    checkedRows,
    activeIndex,
    setActiveIndexNoScroll,
    isKeyboardNavigation,
    setIsKeyboardNavigation,
    isMouseEnabled,
    registerItem,
    navigationData,
    uncheckedData,
    size,
    classes,
    handleItemClick,
  } = useListContext<T>();

  // Use the same data source as keyboard navigation for consistent indexing
  const dataToRender = navigationData || uncheckedData;

  const index = dataToRender.findIndex(
    (item) => getRowKey(item) === getRowKey(value),
  );
  const skipRow = getShouldSkipRow(value);
  const isHighlighted = activeIndex === index;
  const isChecked = isMultiselect && checkedRows[getRowKey(value)];

  const checkedCount = useMemo(
    () => Object.values(checkedRows).filter(Boolean).length,
    [checkedRows],
  );
  const disableUncheck = Boolean(
    isMultiselect &&
    isCheckboxesEnabled &&
    !isNullable &&
    isChecked &&
    checkedCount === 1,
  );

  // Register item on mount
  useEffect(() => {
    if (!ref.current) return;
    const id = getRowKey(value).toString();
    const unregister = registerItem(id, ref.current, value);
    return unregister;
  }, [value, registerItem, getRowKey]);

  // Handle click
  const handleClick = (event?: React.MouseEvent) => {
    if (skipRow) return;

    // Prevent the global keyboard handler from running
    if (event) {
      event.stopPropagation();
    }

    handleItemClick(value, index);
  };

  if ("isDivider" in (value as any) && (value as any).isDivider) {
    return (
      <li ref={ref} data-testid={dataTestId} className={classes.listItem}>
        {children}
      </li>
    );
  }

  return (
    <li
      ref={ref}
      role={skipRow ? "div" : "button"}
      tabIndex={isHighlighted ? 0 : -1}
      data-testid={dataTestId}
      onMouseEnter={() => {
        // Ignore mouse events until mouse has actually moved after keyboard navigation
        if (!isMouseEnabled) return;

        // Reset keyboard navigation flag when mouse moves
        if (isKeyboardNavigation) {
          setIsKeyboardNavigation(false);
        }
        // Set the active index
        setActiveIndexNoScroll(index);
      }}
      onClick={handleClick}
      className={cn(
        // A finger's size, wherever a finger picks from it.
        "grow flex items-center justify-start gap-2 rounded-sm px-2 py-1.5 select-none pointer-coarse:min-h-11",
        // The one the keys or the pointer are on, filled; the rest never.
        isHighlighted
          ? "bg-accent! hover:bg-accent! active:bg-accent!"
          : "bg-transparent! hover:bg-transparent! active:bg-transparent!",
        "hover:text-foreground! active:text-foreground!",
        "focus:[outline:2px_solid_var(--primary)] focus:[outline-offset:2px]",
        !skipRow ? "cursor-pointer" : "cursor-default",
        ROW_TEXT[size],
        classes.listItem,
        className,
      )}
    >
      {children}
      {isMultiselect && isCheckboxesEnabled && (
        <Checkbox
          checked={!!isChecked}
          disabled={disableUncheck}
          className="ml-auto"
        />
      )}
    </li>
  );
}
